import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useDiseases,
  useCreateFamilyDiseaseHistory,
  useDeleteFamilyDiseaseHistory,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import {
  SearchableChipDialog,
  type ChipGroup,
} from "@/components/consultation/shared/SearchableChipDialog";
import type {
  FamilyDiseaseHistory,
  CreateFamilyDiseaseHistoryRequest,
} from "@/types/appointments";

const familyDiseaseHistorySchema = z.object({
  disease: z.string().min(1, "Please select a disease"),
  relations: z.enum(["FATHER", "MOTHER", "SIBLING", "DAUGHTER", "SON"]),
  death_cause: z.string().optional(),
  death_age: z.number().min(-1).max(150).optional(),
});

type FamilyDiseaseHistoryFormData = z.infer<typeof familyDiseaseHistorySchema>;

interface AddFamilyDiseaseHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientFamilyHistory?: FamilyDiseaseHistory[];
}

export const AddFamilyDiseaseHistoryDialog = ({
  open,
  onOpenChange,
  patientId,
  patientFamilyHistory = [],
}: AddFamilyDiseaseHistoryDialogProps) => {
  const [view, setView] = useState<"manage" | "form">("manage");
  const [deathCausePopoverOpen, setDeathCausePopoverOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDiseaseId, setSelectedDiseaseId] = useState<string | null>(
    null
  );
  const { toast } = useToast();

  const { data: diseases = [] } = useDiseases();
  const createMutation = useCreateFamilyDiseaseHistory();
  const deleteMutation = useDeleteFamilyDiseaseHistory();

  const isLoading = createMutation.isPending || deleteMutation.isPending;

  useEffect(() => {
    if (open) {
      setView("manage");
      setSelectedDiseaseId(null);
    }
  }, [open]);

  const form = useForm<FamilyDiseaseHistoryFormData>({
    resolver: zodResolver(familyDiseaseHistorySchema),
    defaultValues: {
      disease: "",
      relations: "FATHER",
      death_cause: "",
      death_age: -1,
    },
  });

  useEffect(() => {
    if (selectedDiseaseId && view === "form") {
      form.setValue("disease", selectedDiseaseId);
    }
    if (!selectedDiseaseId && view === "form") {
      form.reset({
        disease: "",
        relations: "FATHER",
        death_cause: "",
        death_age: -1,
      });
    }
  }, [selectedDiseaseId, view, form]);

  const handleAddClick = (diseaseId: string) => {
    setSelectedDiseaseId(diseaseId);
    setView("form");
  };

  const handleRemoveClick = async (id: string) => {
    const entry = patientFamilyHistory.find((h) => h.id === id);
    if (!entry) return;
    try {
      await deleteMutation.mutateAsync({ historyId: entry.id, patientId });
      toast({
        title: "Success",
        description: "Family disease history removed successfully",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove family disease history",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: FamilyDiseaseHistoryFormData) => {
    try {
      const requestData: CreateFamilyDiseaseHistoryRequest = {
        disease: data.disease,
        relations: data.relations,
        patient: patientId,
        ...(data.death_cause && { death_cause: data.death_cause }),
        ...(data.death_age !== undefined &&
          data.death_age !== -1 && { death_age: data.death_age }),
      };

      await createMutation.mutateAsync(requestData);
      toast({
        title: "Success",
        description: "Family disease history added successfully",
      });

      setView("manage");
      setSelectedDiseaseId(null);
      form.reset();
    } catch {
      toast({
        title: "Error",
        description: "Failed to add family disease history",
        variant: "destructive",
      });
    }
  };

  const selectedDisease = diseases.find((d) => d.id === form.watch("disease"));
  const selectedDeathCause = diseases.find(
    (d) => d.id === form.watch("death_cause")
  );

  // Filter available diseases
  const addedDiseaseIds = new Set(
    patientFamilyHistory.map((h) => h.disease.id)
  );

  const availableDiseases = diseases.filter((disease) => {
    const matchesSearch =
      disease.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      disease.icd10_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const notAdded = !addedDiseaseIds.has(disease.id);
    return matchesSearch && notAdded;
  });

  // Build grouped added items by relation
  const addedGroups: ChipGroup[] = (
    ["FATHER", "MOTHER", "SIBLING"] as const
  ).map((relation) => ({
    key: relation,
    label: `${relation}'s History`,
    items: patientFamilyHistory
      .filter((h) => h.relations === relation)
      .map((h) => ({
        id: h.id,
        label: h.disease.display_name,
      })),
  }));

  const formContent = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {selectedDiseaseId && (
          <div className="p-4 bg-muted/50 rounded-xl border border-border/60 mb-6">
            <h3 className="font-medium text-foreground">
              {selectedDisease?.display_name || "Selected Disease"}
            </h3>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="relations"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Relation</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="FATHER">Father</SelectItem>
                    <SelectItem value="MOTHER">Mother</SelectItem>
                    <SelectItem value="SIBLING">Sibling</SelectItem>
                    <SelectItem value="DAUGHTER">Daughter</SelectItem>
                    <SelectItem value="SON">Son</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="death_age"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Age at Death (optional)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="-1 if not applicable"
                    {...field}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? parseInt(e.target.value) : -1
                      )
                    }
                    value={field.value === -1 ? "" : field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="death_cause"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>Cause of Death (optional)</FormLabel>
              <Popover
                open={deathCausePopoverOpen}
                onOpenChange={setDeathCausePopoverOpen}
                modal={true}
              >
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between"
                    >
                      {field.value
                        ? selectedDeathCause?.display_name
                        : "Select cause of death"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-[480px] p-0 dark" align="start">
                  <Command>
                    <CommandInput placeholder="Search diseases..." />
                    <CommandEmpty>No diseases found.</CommandEmpty>
                    <CommandList>
                      <CommandGroup>
                        <CommandItem
                          value=""
                          onSelect={() => {
                            field.onChange("");
                            setDeathCausePopoverOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              field.value === "" ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="text-muted-foreground">None</span>
                        </CommandItem>
                        {diseases.map((disease) => (
                          <CommandItem
                            key={`${disease.id}-death`}
                            value={disease.id}
                            keywords={[
                              disease.display_name,
                              disease.long_name ?? "",
                              disease.short_name ?? "",
                              disease.icd10_code ?? "",
                            ]}
                            onSelect={(currentValue) => {
                              const nextValue =
                                currentValue === field.value
                                  ? ""
                                  : currentValue;
                              field.onChange(nextValue);
                              setDeathCausePopoverOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                field.value === disease.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground">
                                {disease.display_name}
                              </span>
                              {disease.icd10_code && (
                                <span className="text-xs text-muted-foreground">
                                  ICD-10: {disease.icd10_code}
                                </span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setView("manage")}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Add Family History"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );

  return (
    <SearchableChipDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Family History"
      addedGroups={addedGroups}
      onRemoveItem={handleRemoveClick}
      searchLabel="Add family history here"
      searchPlaceholder="Search Here"
      searchResults={availableDiseases.map((d) => ({
        id: d.id,
        label: d.display_name,
      }))}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      minSearchChars={2}
      emptySearchMessage="No matching diseases found"
      onAddItem={handleAddClick}
      formView={formContent}
      showForm={view === "form"}
      onBackToManage={() => setView("manage")}
    />
  );
};
